import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import { REGISTRANT_BULK_LICENSE_THRESHOLD } from "@/components/registrant/dashboard/registrant-bulk-registrations-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type {
  RegistrantLicenseListQuestionColumnSchema,
  UnregisteredPropertyListResponseSchema,
  UnregisteredPropertyResponseSchema,
} from "@/hera-api/client/types.gen";
import { listableRegistrantQuestionColumns } from "@/lib/listable-answer-types";
import { registrantDisplayName } from "@/lib/registrant-display";
import { formatDateNoTimezone, formatDateTime } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import { useMemo } from "react";

const QCOL_PREFIX = "qcol:";

const STATIC_KEYS = [
  "jurisdiction_name",
  "property_address",
  "jurisdiction_display_code",
  "city",
  "county",
  "state",
  "zipcode",
  "apn",
  "license_type_name",
  "effective_date",
  "owner_name",
  "owner_address",
  "owner_email",
  "owner_phone",
  "registrant_display_name",
  "created_at",
  "last_updated_at",
] as const;

function propertyAddress(row: UnregisteredPropertyResponseSchema): string {
  const property = row.property;
  if (!property) return "";
  const street = [property.address1, property.address2]
    .filter(Boolean)
    .join(" ");
  const locality = [property.city, property.state, property.zipcode]
    .filter(Boolean)
    .join(" ");
  return [street, locality].filter(Boolean).join(", ");
}

function ownerAddress(row: UnregisteredPropertyResponseSchema): string {
  const street = [row.owner_address1, row.owner_address2]
    .filter(Boolean)
    .join(" ");
  const locality = [row.owner_city, row.owner_state, row.owner_zip]
    .filter(Boolean)
    .join(" ");
  return [street, locality].filter(Boolean).join(", ");
}

function registrantLabel(row: UnregisteredPropertyResponseSchema): string {
  const account = row.registrant_account;
  if (!account) return "";
  const label = registrantDisplayName(account);
  return label ? `${label} (${account.email})` : account.email;
}

function questionColumn(
  columns: RegistrantLicenseListQuestionColumnSchema[],
  key: string,
) {
  return columns.find((column) => column.key === key);
}

function getStaticValue(
  row: UnregisteredPropertyResponseSchema,
  key: string,
): string {
  switch (key) {
    case "jurisdiction_name":
      return row.jurisdiction.name;
    case "jurisdiction_display_code":
      return row.jurisdiction.display_code;
    case "property_address":
      return propertyAddress(row);
    case "city":
      return row.property?.city ?? "";
    case "county":
      return row.jurisdiction.county ?? "";
    case "state":
      return row.property?.state ?? row.jurisdiction.state ?? "";
    case "zipcode":
      return row.property?.zipcode ?? "";
    case "apn":
      return row.property?.apn ?? "";
    case "license_type_name":
      return row.license_type.name;
    case "effective_date":
      return row.effective_date;
    case "owner_name":
      return row.owner_name ?? "";
    case "owner_address":
      return ownerAddress(row);
    case "owner_email":
      return row.owner_email ?? "";
    case "owner_phone":
      return row.owner_phone ?? "";
    case "registrant_display_name":
      return registrantLabel(row);
    case "created_at":
      return row.created_at;
    case "last_updated_at":
      return row.last_updated_at;
    default:
      return "";
  }
}

interface DashboardRegistrantUnregisteredProps {
  data: UnregisteredPropertyListResponseSchema;
  showRegistrantColumn: boolean;
  columnFilters: Record<string, string[]>;
  onColumnFiltersChange: (filters: Record<string, string[]>) => void;
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  pageIndex: number;
  pageSize: number;
  onPaginationChange: (pagination: {
    pageIndex: number;
    pageSize: number;
  }) => void;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  isRefreshing?: boolean;
}

export function DashboardRegistrantUnregistered({
  data,
  showRegistrantColumn,
  columnFilters,
  onColumnFiltersChange,
  columnVisibility,
  onColumnVisibilityChange,
  pageIndex,
  pageSize,
  onPaginationChange,
  sorting,
  onSortingChange,
  isRefreshing = false,
}: DashboardRegistrantUnregisteredProps) {
  const questionColumns = listableRegistrantQuestionColumns(
    data.question_columns ?? [],
  );
  const accessorKeys = useMemo(
    () => [
      ...STATIC_KEYS.filter(
        (key) => key !== "registrant_display_name" || showRegistrantColumn,
      ),
      ...questionColumns.map((column) => `${QCOL_PREFIX}${column.key}`),
    ],
    [questionColumns, showRegistrantColumn],
  );

  const getColumnProperties = useMemo(
    () =>
      (
        accessorKey: string,
      ): ColumnProperties<UnregisteredPropertyResponseSchema, string> => {
        const common = {
          filterValues: data.faceted_values?.[accessorKey] ?? [],
        };
        if (accessorKey.startsWith(QCOL_PREFIX)) {
          const key = accessorKey.slice(QCOL_PREFIX.length);
          const column = questionColumn(questionColumns, key);
          return {
            ...common,
            displayName: column?.label ?? key,
            defaultVisible: column?.default_visible ?? false,
            getValue: (row) => row.question_column_values?.[key] ?? "",
          };
        }
        const labels: Record<string, string> = {
          jurisdiction_name: "Jurisdiction",
          jurisdiction_display_code: "Jurisdiction #",
          property_address: "Property",
          city: "City",
          county: "County",
          state: "State",
          zipcode: "ZIP",
          apn: "APN",
          license_type_name: "License Type",
          effective_date: "Effective Date",
          owner_name: "Owner",
          owner_address: "Owner Address",
          owner_email: "Owner Email",
          owner_phone: "Owner Phone",
          registrant_display_name: "Linked User",
          created_at: "Created",
          last_updated_at: "Last Updated",
        };
        const defaultVisible = new Set([
          "property_address",
          "county",
          "state",
          "license_type_name",
          "effective_date",
          "registrant_display_name",
        ]);
        const isDate = [
          "effective_date",
          "created_at",
          "last_updated_at",
        ].includes(accessorKey);
        const alwaysFilled = new Set([
          "jurisdiction_name",
          "jurisdiction_display_code",
          "license_type_name",
          "effective_date",
          "created_at",
          "last_updated_at",
        ]);
        return {
          ...common,
          displayName: labels[accessorKey] ?? accessorKey,
          defaultVisible: defaultVisible.has(accessorKey),
          allowBlankFilter: alwaysFilled.has(accessorKey) ? false : undefined,
          getValue: (row) => getStaticValue(row, accessorKey),
          filterType: isDate ? "dateRange" : "values",
          renderCell: isDate
            ? (row) =>
                accessorKey === "effective_date"
                  ? formatDateNoTimezone(row.effective_date)
                  : formatDateTime(getStaticValue(row, accessorKey))
            : undefined,
        };
      },
    [data.faceted_values, questionColumns],
  );

  const grouped = useMemo(() => {
    const groups = new Map<
      string,
      { name: string; rows: UnregisteredPropertyResponseSchema[] }
    >();
    for (const row of data.unregistered) {
      const existing = groups.get(row.jurisdiction.display_code);
      if (existing) existing.rows.push(row);
      else {
        groups.set(row.jurisdiction.display_code, {
          name: row.jurisdiction.name,
          rows: [row],
        });
      }
    }
    return [...groups.entries()].sort((a, b) =>
      a[1].name.localeCompare(b[1].name),
    );
  }, [data.unregistered]);

  const renderRegisterAction = (row: UnregisteredPropertyResponseSchema) => (
    <Button asChild size="sm">
      <Link
        to="/registrant/new-license"
        search={{
          licenseTypeId: row.license_type.id,
          jurisdictionId: row.jurisdiction.id,
          unregisteredId: row.id,
        }}
      >
        Register
      </Link>
    </Button>
  );

  const criteria = (
    <>
      <p className="text-sm text-muted-foreground">
        Active properties the jurisdiction has identified that are not yet
        registered to your organization.
      </p>
    </>
  );

  if ((data.unfiltered_total_count ?? data.total_count ?? 0) === 0) {
    return (
      <div className="space-y-4">
        {criteria}
        <p className="text-sm text-muted-foreground">
          No active unregistered properties are linked to your organization for
          the selected filters.
        </p>
      </div>
    );
  }

  const useBulk =
    (data.organization_total_count ??
      data.unfiltered_total_count ??
      data.total_count ??
      0) > REGISTRANT_BULK_LICENSE_THRESHOLD;
  const tableProps = {
    accessorKeys,
    getColumnProperties,
    getRowId: (row: UnregisteredPropertyResponseSchema) => row.id,
    columnFilters,
    onColumnFiltersChange,
    columnVisibility,
    onColumnVisibilityChange,
    renderRowActions: renderRegisterAction,
    rowActionsColumnWidth: 110,
    tablePersistenceKey: "registrant-unregistered-properties",
  };

  if (useBulk) {
    return (
      <DashboardTable
        {...tableProps}
        data={data.unregistered}
        serverMode
        rowCount={data.total_count ?? 0}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPaginationChange={onPaginationChange}
        sorting={sorting}
        onSortingChange={onSortingChange}
        isRefreshing={isRefreshing}
        leadingContent={<div className="space-y-4">{criteria}</div>}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
      {criteria}
      {grouped.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No unregistered properties match the current filters.
        </p>
      ) : null}
      {grouped.map(([code, group]) => (
        <Card key={code} className="min-w-0">
          <CardHeader className="px-4 md:px-6">
            <CardTitle className="font-krona-one uppercase text-xl leading-none tracking-tight font-bold">
              {group.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="min-w-0 px-4 md:px-6">
            <DashboardTable
              {...tableProps}
              data={group.rows}
              lockToViewport={false}
            />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
